import { LogInterface } from '@evorait/pace-logging';
import { ODataObject } from '../../lib/interfaces/odata-model.interface';
import { isDefined } from '../utilities/is-defined';

export class TextInputUtilities {
    public static readonly URL_REGEX = /(\b(https|http?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
    /**
     * Take an array of longtext items and extract the longtext contained within.
     * An optional joinToken is used to join the lines.
     *
     * @param lines
     * @param joinToken
     * @param log
     */
    public static getLongtext(lines: ODataLTextModel[], joinToken = ' ', log: LogInterface): string {
        const strings = this.getLongArray(lines, log).map((line) => line.TextLine);
        return strings.join(joinToken);
    }

    /**
     * Processes an array of text lines, handling [url] and [/url] tags that may
     * span across multiple lines, and returns a new array with formatted URLs.
     *
     * @param lines - Raw long text lines
     * @param log - Logger instance
     */
    public static getLongArray(lines: ODataLTextModel[], log: LogInterface): ODataLTextModel[] {
        const result: ODataLTextModel[] = [];
        let urlBuffer = '';
        let counter = 1;
        const urlStartTag = '[url]';
        const urlEndTag = '[/url]';

        // Sort lines by counter to preserve logical order
        const sortedLines = [...lines].sort((a, b) => a.Counter - b.Counter);

        // Finalizes the current URL when [/url] is found.
        // Adds the formatted link and any trailing text.
        const finalizeUrl = (textSegment: string, line: ODataLTextModel): void => {
            const [urlPart, trailingText = ''] = textSegment.split(urlEndTag);
            urlBuffer += urlPart;

            const formattedUrl = `<a href="${urlBuffer}" class="pace-link" target="_blank">${urlBuffer}</a>`;
            result.push({ ...line, TextLine: formattedUrl, Counter: counter++ });
            urlBuffer = '';

            if (trailingText.trim()) {
                result.push({ ...line, TextLine: trailingText, Counter: counter++ });
            }
        };

        // Handles lines containing [url], which may or may not close in the same line.
        const processUrlLine = (line: ODataLTextModel): void => {
            const parts = line.TextLine.split(urlStartTag);

            parts.forEach((segment, index) => {
                if (segment.includes(urlEndTag)) {
                    finalizeUrl(segment, line);
                } else if (index === 0 && !urlBuffer) {
                    if (segment.trim()) {
                        result.push({ ...line, TextLine: segment, Counter: counter++ });
                    }
                } else {
                    urlBuffer += segment;
                }
            });
        };

        for (const [index, line] of sortedLines.entries()) {
            const text = line.TextLine;
            if (text === null) {
                log.error(`TextInputUtilities: getLongArray: error: ${JSON.stringify(line)}`);
                continue;
            }

            if (text.includes(urlStartTag)) {
                processUrlLine(line);
            } else if (text.includes(urlEndTag) || (index === sortedLines.length - 1 && urlBuffer.length > 0)) {
                finalizeUrl(text, line);
            } else if (urlBuffer.length === 0) {
                result.push({ ...line, Counter: counter++ });
            } else {
                urlBuffer += text;
            }
        }
        return result;
    }

    public static wrapUrlsInText(text: string): string {
        if (!text) {
            return '';
        }

        return text.replace(this.URL_REGEX, '[url]$1[/url]');
    }

    public static splitLineByUrls(line: string): string[] {
        const result: string[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = this.URL_REGEX.exec(line)) !== null) {
            const urlStart = match.index;
            const urlEnd = urlStart + match[0].length;

            if (urlStart > lastIndex) {
                result.push(line.slice(lastIndex, urlStart));
            }

            result.push(match[0]);

            lastIndex = urlEnd;
        }

        if (lastIndex < line.length) {
            result.push(line.slice(lastIndex));
        }

        return result;
    }

    /**
     * Wraps a line of text into multiple lines based on the provided regex pattern.
     * This ensures each line does not exceed the maximum character limit required by SAP,
     * as SAP backend and service reports can only accommodate a fixed number of characters per line.
     *
     * @param line - The line of text to wrap.
     * @param pattern - RegExp defining max characters per line (controlled from Gateway).
     * @returns Array of lines, each conforming to the SAP character limit.
     */
    public static wrapTextToNewLine(line: string, pattern: RegExp): string[] {
        const results: string[] = [];

        while (line.length > 0) {
            const match = line.match(pattern);

            if (!isDefined(match)) {
                break;
            }

            results.push(match[0]);
            line = line.slice(match[0].length).trimStart();
        }

        return results;
    }
}

export interface ODataLTextModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
    NotificationNo: string;
    TaskNumber: string;
    TextID: string;
    TextLine: string;
    Counter: number;
}
